import { subHours } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

import { SpaceBookingPolicy } from "../data";
import { TemplateName } from "../data/partner_offering";
import { FBT, Locale } from "../lib/i18n/i18n_provider";
import { Quote } from "./api";
import { parseDay, setSameDay, subDays, toDay } from "./day_utils";
import { OrderParams } from "./order_params";
import { parseTime } from "./time_utils";
import humanizeDuration from "humanize-duration";
import { isHourlyValue } from "@/offering_v2/components/date-time-picker/date_time_picker";

export function getCheckoutCancellationPolicyText(
  fbt: FBT,
  locationTimezone: string,
  spaceBookingPolicy?: SpaceBookingPolicy,
  orderParams?: OrderParams,
  locale?: Locale,
  quote?: Quote
): string {
  // Check non refundable promotion
  if (hasNonRefundablePromotion(quote)) return fbt("Non refundable.");

  // Check dynamic price rate order_non_refundable
  if (hasDynamicPriceRateOrderNonRefundable(quote))
    return fbt("Non refundable.");

  if (!orderParams) return "";

  if (orderParams.type === "Membership") {
    return fbt("Cancellable anytime with a 30 days notice.");
  }

  if (!spaceBookingPolicy) return "";

  if (
    orderParams.type === TemplateName.MultiPass ||
    orderParams.type === TemplateName.MultiDayOffice ||
    (orderParams.type === "DailyAccess" && orderParams.addOnID !== "daypass") ||
    (orderParams.type === "DailyOffice" && orderParams.addOnID !== "dayoffice")
  ) {
    return fbt("Non refundable.");
  }

  if (spaceBookingPolicy?.cancellationWarning) {
    return spaceBookingPolicy?.cancellationWarning;
  }

  const { cancellationTime1DayBefore, cancellationHours, cancellationDays } =
    spaceBookingPolicy;

  const date =
    (orderParams.type === "Hourly" ||
      orderParams.type === "MeetingRoom" ||
      orderParams.type === "EventSpace") &&
    isHourlyValue(orderParams.dateRange)
      ? orderParams.dateRange?.date
      : !isHourlyValue(orderParams.dateRange)
      ? orderParams.dateRange?.startDate
      : undefined;

  if (!date) {
    return "";
  }

  // cancellation exact 1 date before
  if (cancellationTime1DayBefore) {
    return getCancellation1DayBeforeText(
      cancellationTime1DayBefore,
      locationTimezone,
      date,
      fbt
    );
  }

  // cancellation certain number of hours before
  if (cancellationHours) {
    return getCancellationHoursBeforeText(
      cancellationHours,
      locationTimezone,
      date,
      fbt,
      date,
      locale
    );
  }

  // cancellation certain number of days before
  if (cancellationDays) {
    return getCancellationDaysBeforeText(
      cancellationDays,
      locationTimezone,
      date,
      fbt,
      date,
      locale
    );
  }

  // cancellation not configured
  return fbt("Non refundable.");
}

function hasDynamicPriceRateOrderNonRefundable(quote?: Quote): boolean {
  if (!quote) return false;

  const singleDayDynamicPriceRate = quote.quoteItems?.map(
    (qi) => qi.dynamicPriceRate
  );

  const multipleDaysDynamicPriceRate = quote.quoteItems
    ?.map((qi) => qi.quoteItemPrices?.map((qip) => qip.dynamicPriceRate))
    ?.flat();

  const hasDynamicPriceRateOrderNonRefundable =
    singleDayDynamicPriceRate?.some((d) => d?.orderNonRefundable) ||
    multipleDaysDynamicPriceRate?.some((d) => d?.orderNonRefundable);

  if (hasDynamicPriceRateOrderNonRefundable) {
    return true;
  }

  return false;
}

function hasNonRefundablePromotion(quote?: Quote): boolean {
  if (!quote) return false;

  return quote.quoteItems?.some((qi) => qi.promotion?.orderNonRefundable);
}

function getCancellation1DayBeforeText(
  cancellationTime1DayBefore: string,
  locationTimezone: string,
  date: string,
  fbt: FBT
): string {
  const cancellableUntilDate = subDays(date, 1);
  const cancellableUntilTime = setSameDay(
    parseTime(cancellationTime1DayBefore),
    cancellableUntilDate
  );

  if (
    zonedTimeToUtc(cancellableUntilTime, locationTimezone).getTime() <=
    new Date().getTime()
  ) {
    return fbt("Non refundable.");
  }
  return fbt(
    "Free cancellation before " +
      fbt.param("time", fbt.formatTime(cancellableUntilTime)) +
      " on " +
      fbt.param("date", fbt.formatDate(parseDay(cancellableUntilDate)))
  );
}

function getCancellationHoursBeforeText(
  cancellationHours: number,
  locationTimezone: string,
  date: string,
  fbt: FBT,
  bookingTime?: string | null,
  locale?: Locale
): string {
  if (bookingTime) {
    const time = setSameDay(parseTime(bookingTime), date);
    const cancellableUntilTime = subHours(time, cancellationHours);
    if (
      zonedTimeToUtc(cancellableUntilTime, locationTimezone).getTime() <=
      new Date().getTime()
    ) {
      return fbt("Non refundable.");
    }
  }

  const duration = humanizeDuration(cancellationHours * 1000 * 3600, {
    units: ["h"],
    round: true,
    language: locale?.baseLanguageTag,
    fallbacks: ["en"],
  });

  return fbt(
    "Cancel for free up to " +
      fbt.param("duration", duration) +
      " before your booking"
  );
}

function getCancellationDaysBeforeText(
  cancellationDays: number,
  locationTimezone: string,
  date: string,
  fbt: FBT,
  bookingTime?: string | null,
  locale?: Locale
): string {
  if (bookingTime) {
    const bookingDate = setSameDay(parseTime(bookingTime), date);
    const cancellableUntilDay = subDays(toDay(bookingDate), cancellationDays);

    if (
      zonedTimeToUtc(cancellableUntilDay, locationTimezone).getTime() <=
      new Date().getTime()
    ) {
      return fbt("Non refundable.");
    }
  }

  const duration = humanizeDuration(cancellationDays * 24 * 60 * 60 * 1000, {
    units: ["d"],
    round: true,
    language: locale?.baseLanguageTag,
    fallbacks: ["en"],
  });

  return fbt(
    "Cancel for free up to " +
      fbt.param("duration", duration) +
      " before your booking"
  );
}
